import type { Path, Point } from 'slate';
import { Editor, Node, Range, Transforms } from 'slate';

import SelectionTransforms from './selection-transforms';
import type {
  CrossReferenceElement,
  EditingMode,
  SuggestedInstructionFieldEdit,
} from '../internal';
import {
  AlineTransforms,
  bookmarkStartType,
  createBookmarkEnd,
  createBookmarkStart,
  createDefaultText,
  EditingMode as EditingModeEnum,
  instructionFieldElementType,
  isBookmarkStartElement,
  isCrossReferenceElement,
  isParagraphElement,
  Queries,
  SelEventSource,
} from '../internal';

/**
 * Where a new cross reference points. A `bookmark` target references an
 * existing bookmark by name; a `paragraph` target references a heading or
 * numbered item, wrapping it in a backing bookmark first
 */
export type InsertCrossReferenceTarget =
  | { kind: 'bookmark'; name: string }
  | { kind: 'paragraph'; path: Path };

const BookmarkTransforms = {
  /**
   * Insert a new cross-reference field at the current selection.
   */
  insertCrossReference: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      instruction: CrossReferenceElement['instruction'];
      switches: string;
      target: InsertCrossReferenceTarget;
    }
  ): boolean => {
    const { authorId, editingMode, instruction, switches, target } = options;
    if (!editor.selection) return false;

    const pageNumberText = resolveInsertTimePageNumber(editor, { instruction, switches, target });
    const pointRef = Editor.pointRef(editor, Range.start(editor.selection));

    const fieldPathRef = AlineTransforms.withoutNormalizing(editor, () => {
      if (!pointRef.current) return;

      const bookmarkName =
        target.kind === 'bookmark' ? target.name : ensureParagraphBookmark(editor, target.path);

      const at = pointRef.unref();
      if (!at || !bookmarkName) return;

      const formats = Queries.formatsAtCursor(editor, { at }) ?? undefined;
      const isSuggesting = editingMode === EditingModeEnum.SUGGESTING;
      const insertEdit: SuggestedInstructionFieldEdit = {
        authorId,
        instruction,
        type: 'insertInstructionField',
        value: {},
      };

      const href = `#${bookmarkName}`;
      const element: CrossReferenceElement = {
        children: [createDefaultText(formats)],
        href,
        instruction,
        suggestedEdits: isSuggesting ? [insertEdit] : [],
        switches,
        type: instructionFieldElementType,
      };

      // Insert the (empty-text) field.
      AlineTransforms.insertContentNodes(editor, {
        at,
        authorId,
        editingMode,
        nodes: [element],
        processEdits: false,
        select: 'end',
      });

      /**
       * Resolve display text using the real inserted path, then fill the void's
       * child text node in place
       */
      const entry = editor.selection
        ? Editor.above<CrossReferenceElement>(editor, {
            at: editor.selection,
            match: node => isCrossReferenceElement(node) && node.href === href,
            voids: true,
          })
        : undefined;
      if (!entry) return;

      const [insertedNode, insertedPath] = entry;
      const text =
        pageNumberText ?? Queries.getCrossReferenceText(editor, insertedNode, insertedPath);
      if (text) {
        Transforms.insertText(editor, text, {
          at: Editor.start(editor, insertedPath),
          voids: true,
        });
      }

      return Editor.pathRef(editor, insertedPath);
    });

    const fieldPath = fieldPathRef?.unref();
    if (!fieldPath) return false;

    const afterField = Editor.after(editor, fieldPath);
    if (afterField) Transforms.select(editor, afterField);

    return true;
  },

  /**
   * Remove a cross-reference field, taking into account editing mode.
   */
  removeCrossReference: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      path: Path;
    }
  ): void => {
    const { authorId, editingMode, path } = options;

    AlineTransforms.withoutNormalizing(editor, () => {
      const node = Node.get(editor, path);
      if (!isCrossReferenceElement(node)) return;

      if (editingMode === EditingModeEnum.SUGGESTING) {
        AlineTransforms.suggestDeletion(editor, { at: path, authorId, editingMode });
      } else {
        Transforms.removeNodes(editor, { at: path, voids: true });
      }
    });
  },

  /**
   * Select the start of a bookmark if it exists.
   */
  selectBookmark: (editor: Editor, bookmarkName: string): boolean => {
    const start = Queries.getBookmarkStart(editor, bookmarkName);
    if (!start) return false;

    SelectionTransforms.setSelection(editor, {
      eventSource: SelEventSource.PROGRAMMATIC,
      props: { affinity: 'forward', anchor: start, focus: start },
    });

    return true;
  },

  /**
   * Update all cross-reference fields within a location range.
   * Defaults to the current selection if no location is provided.
   */
  updateAllCrossReferences: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
    }
  ): void => {
    const { authorId, editingMode } = options;
    const { selection } = editor;
    const at = selection !== null && !Range.isCollapsed(selection) ? selection : [];

    AlineTransforms.withoutNormalizing(editor, () => {
      const crossRefEntries = [
        ...Editor.nodes<CrossReferenceElement>(editor, {
          at,
          match: isCrossReferenceElement,
          voids: true,
        }),
      ];

      for (const [crossRef, path] of crossRefEntries.reverse()) {
        const newText = Queries.getCrossReferenceText(editor, crossRef, path);
        replaceCrossReference(editor, crossRef, path, newText, authorId, editingMode);
      }
    });
  },

  /**
   * Update the display text of a single cross-reference field.
   * Resolves the field value based on the instruction type (REF, PAGEREF, NOTEREF) and switches.
   */
  updateCrossReferenceText: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      path: Path;
    }
  ): void => {
    const { authorId, editingMode, path } = options;
    const node = Node.get(editor, path);

    if (isCrossReferenceElement(node)) {
      const newText = Queries.getCrossReferenceText(editor, node, path);
      replaceCrossReference(editor, node, path, newText, authorId, editingMode);
    }
  },
};

export default BookmarkTransforms;

/**
 * Return the name of a bookmark wrapping the paragraph at `paragraphPath`.
 * Reuses an existing bookmark already inside the paragraph; otherwise creates a
 * new `_Ref<markerId>` bookmark wrapping the paragraph's content.
 */
function ensureParagraphBookmark(editor: Editor, paragraphPath: Path): string {
  const paragraph = Node.get(editor, paragraphPath);
  if (!isParagraphElement(paragraph)) return '';

  const existing = paragraph.children.find(isBookmarkStartElement);
  if (existing) return existing.name;

  const markerId = Queries.getNextMarkerId(editor, bookmarkStartType);
  const name = `_Ref${markerId}`;

  Transforms.insertNodes(editor, createBookmarkEnd(markerId), {
    at: [...paragraphPath, paragraph.children.length],
  });
  Transforms.insertNodes(editor, createBookmarkStart(markerId, name), {
    at: [...paragraphPath, 0],
  });

  return name;
}

/**
 * Resolve a PAGEREF's page number from the target's CURRENT layout
 */
function resolveInsertTimePageNumber(
  editor: Editor,
  options: {
    instruction: CrossReferenceElement['instruction'];
    switches: string;
    target: InsertCrossReferenceTarget;
  }
): string | null {
  const { instruction, switches, target } = options;
  if (instruction !== 'PAGEREF' || switches.includes('\\p')) return null;

  let targetPoint: Point | null;
  if (target.kind === 'paragraph') {
    targetPoint = Node.has(editor, target.path) ? Editor.start(editor, target.path) : null;
  } else {
    targetPoint = Queries.getBookmarkStart(editor, target.name);
  }
  if (!targetPoint) return null;

  const pageNumber = Queries.getPageNumberForCMPoint(editor, targetPoint);
  return pageNumber === null ? null : String(pageNumber);
}

/**
 * Replace a cross-reference element with an updated copy containing new text.
 *
 * Follows the same pattern as page-numbering-transforms.ts:
 * - Delete: suggestDeletion() in suggesting mode, removeNodes() in editing mode
 * - Insert: new element with insertInstructionField edit in suggesting mode, clean element in editing mode
 */
function replaceCrossReference(
  editor: Editor,
  crossRef: CrossReferenceElement,
  crossRefPath: Path,
  newText: string,
  authorId: string,
  editingMode: EditingMode
): void {
  const existingText = crossRef.children[0]?.text ?? '';
  if (existingText === newText) return;

  const isSuggesting = editingMode === EditingModeEnum.SUGGESTING;

  const insertEdit: SuggestedInstructionFieldEdit = {
    authorId,
    instruction: crossRef.instruction,
    type: 'insertInstructionField',
    value: {},
  };

  const updatedElement: CrossReferenceElement = {
    ...crossRef,
    children: [{ ...crossRef.children[0], suggestedEdits: [], text: newText }],
    suggestedEdits: isSuggesting ? [insertEdit] : [],
    type: instructionFieldElementType,
  };

  Editor.withoutNormalizing(editor, () => {
    if (isSuggesting) {
      AlineTransforms.suggestDeletion(editor, { at: crossRefPath, authorId, editingMode });
    } else {
      Transforms.removeNodes(editor, { at: crossRefPath, voids: true });
    }

    Transforms.insertNodes(editor, updatedElement, { at: crossRefPath, voids: true });
  });
}
